import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { powerLocationById } from '../../data/power';
import { POWER_STATE_LABEL } from '../../data/power';
import { freshnessLabel } from '../../utils/power';
import { IconTile, LOCATION_ICON, PowerSkeleton, powerIcons, stateTile } from './PowerPrimitives';
import type { PowerIconName } from './PowerPrimitives';
import type { PowerState } from '../../types/power';
interface Tile {
  key: string;
  label: string;
  locationId: string | null;
  /** Shown instead of the location name for the person's own places. */
  displayName?: string;
  icon: PowerIconName;
}

/**
 * Power overview — the six places that matter, each stating its own freshness.
 * A tile with no recent evidence says so; it never falls back to "ON".
 */
export function PowerOverview({
  onReport
}: {
  onReport: (locationId: string) => void;
}) {
  const {
    homeLocationId,
    campusLocationId,
    next
  } = usePower();
  const tiles: Tile[] = [{
    key: 'you',
    label: 'You are here',
    locationId: campusLocationId,
    icon: 'power'
  }, {
    key: 'home',
    label: 'Accommodation',
    locationId: homeLocationId,
    icon: 'accommodation'
  }, {
    key: 'campus',
    label: 'Campus',
    locationId: campusLocationId,
    icon: 'campus'
  }, {
    key: 'class',
    label: 'Next class',
    locationId: next?.locationId ?? null,
    displayName: next?.locationId ? undefined : next?.venue,
    icon: 'class'
  }, {
    key: 'library',
    label: 'Library',
    locationId: 'pl-library',
    icon: 'library'
  }, {
    key: 'centre',
    label: 'Student Centre',
    locationId: 'pl-student-centre',
    icon: 'studentCentre'
  }];
  return <section aria-labelledby="power-overview-heading" className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4228" data-mp="14dcdv6aug727">
      <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-4229" data-mp="18dwdqhmsxos1">
        <h2 id="power-overview-heading" className="text-h4 font-semibold text-ink" data-id="element-4230" data-mp="3b24omcwkn1ef">
          Power overview
        </h2>
        <Link to="/settings" className="text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4231" data-mp="369uzgz3a65d7">
          Customise
        </Link>
      </div>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-id="element-4232" data-mp="2wd8gygyffqec">
        {tiles.map(tile => <li key={tile.key} data-id="element-4233" data-mp="3u10fpfd4uzo1">
            <OverviewTile tile={tile} onReport={onReport} data-id="element-4234" data-mp="3tz5qvpyqqneo" />
          </li>)}
      </ul>
    </section>;
}
function OverviewTile({
  tile,
  onReport
}: {
  tile: Tile;
  onReport: (locationId: string) => void;
}) {
  const {
    getLocationStatus
  } = usePower();
  const location = tile.locationId ? powerLocationById(tile.locationId) : undefined;

  // No matching location entity — say what is missing and offer the one useful
  // action, rather than rendering an empty box or guessing a status.
  if (!location) {
    return <div className="flex h-full flex-col rounded-xl border border-line bg-canvas p-4" data-id="element-4235" data-mp="24nwzt9af3aqi">
        <IconTile name={tile.icon} size="sm" data-id="element-4236" data-mp="k1f4eym065v0" />
        <p className="mt-2.5 text-caption font-medium uppercase tracking-wide text-ink-subtle" data-id="element-4237" data-mp="o4brp2mhmq5a">
          {tile.label}
        </p>
        <p className="mt-0.5 text-body-sm text-ink" data-id="element-4238" data-mp="t2n2bfklx4ok">
          {tile.displayName ?? 'Not set'}
        </p>
        <p className="mt-1 text-caption text-ink-subtle" data-id="element-4239" data-mp="xw7p0bgr2c8g">Not a mapped Campuzon location</p>
      </div>;
  }
  const status = getLocationStatus(location.id);
  const Icon = powerIcons[LOCATION_ICON[location.kind]];
  return <div className="flex h-full flex-col rounded-xl border border-line bg-surface p-4 transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-4240" data-mp="1buo7t0rvzt6y">
      <div className="flex items-start justify-between gap-2" data-id="element-4241" data-mp="1gshedux3qp7r">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${stateTile(status.state)}`} aria-hidden data-id="element-4242" data-mp="3dmkhs5yeouzm">
          <Icon className="h-4 w-4" data-id="element-4243" data-mp="2naywdqcqd0gw" />
        </span>
        <p className="text-caption font-medium uppercase tracking-wide text-ink-subtle" data-id="element-4244" data-mp="2n9ny26220kx6">
          {tile.label}
        </p>
      </div>
      <p className="mt-3 truncate text-body-sm font-medium text-ink" title={location.name} data-id="element-4245" data-mp="2ie8hv7gkqcxl">
        {location.name}
      </p>
      <PowerStateHeadline state={status.state} data-id="element-4246" data-mp="2s9im81ug2wej" />
      <div className="mt-auto pt-2" data-id="element-4247" data-mp="2satotyc5ly42">
        {status.lastConfirmedAt ? <p className="cz-num text-caption text-ink-subtle" data-id="element-4248" data-mp="24litiwdkovxb">
            {freshnessLabel(status.lastConfirmedAt)}
          </p> : <button type="button" onClick={() => onReport(location.id)} className="inline-flex items-center gap-1 text-caption font-medium text-brand-700 hover:underline" data-id="element-4249" data-mp="1zjjqxopnq31f">
            Be the first to report
            <ArrowRightIcon className="h-3 w-3" aria-hidden data-id="element-4250" data-mp="1lsy8u2811x4l" />
          </button>}
      </div>
    </div>;
}

/** The state as the largest thing in the tile — the answer, not the metadata. */
function PowerStateHeadline({
  state
}: {
  state: PowerState;
}) {
  const tone: Record<PowerState, string> = {
    on: 'text-ok-800',
    off: 'text-danger-800',
    unstable: 'text-warn-800',
    backup: 'text-info-800',
    unknown: 'text-ink-muted'
  };
  return <p className={`mt-0.5 text-h3 font-semibold leading-tight ${tone[state]}`} data-id="element-4251" data-mp="3nivz82g52uul">
      {state === 'unknown' ? 'No data' : POWER_STATE_LABEL[state]}
    </p>;
}
export function PowerOverviewSkeleton() {
  return <div className="rounded-2xl border border-line bg-surface p-5 shadow-panel" data-id="element-4252" data-mp="k2yljjirr4ri">
      <PowerSkeleton className="h-6 w-36" data-id="element-4253" data-mp="62xm88i5k5e5" />
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-id="element-4254" data-mp="61vz35nv3sg2">
        {Array.from({
        length: 6
      }).map((_, index) => <PowerSkeleton key={index} className="h-[7.5rem]" data-id="element-4255" data-mp="a6c1gwy9ohqj" data-mpi={index} />)}
      </div>
    </div>;
}